import { useEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { DocumentDto, DocumentEntityType } from '@tww/shared';
import { fmtDate, fmtNum } from '@tww/shared';
import { api, qs } from '../api/client';
import { useLookup } from '../api/hooks';
import { useToast } from './Toast';
import { Icon } from './Icon';
import { ConfirmDialog, Modal } from './Modal';
import { Select, TextInput } from './fields';
import { EmptyState, ErrorState, Skeleton } from './basics';
import { FileIcon, FilePreview, KIND_NAME, fileKind } from './FilePreview';

/**
 * Dateien je Datensatz mit Ordnerstruktur: Ordnerbaum links, Inhalt rechts. Neben dem eigenen Ordner erscheinen die
 * Ordner aller verknüpften Objekte (Förster, Partner, Fläche, Protokoll, Aktionen …) als eigene Wurzeln.
 * Explorer-Bedienung: Mehrfachauswahl (Kästchen, Strg- und Umschalt-Klick), Kontextmenü (rechte Maustaste),
 * Drag-and-Drop für Dateien und Ordner (auch zwischen Objekten), Upload per Ziehen aus dem Explorer, Vorschau per Klick.
 */
interface RelatedRoot {
  entity_type: DocumentEntityType;
  entity_id: number;
  label: string;
  relation: string;
  href: string;
  can_write: boolean;
}
interface Root extends RelatedRoot {
  key: string;
  own: boolean;
}
interface Cur {
  root: string;
  path: string;
}
/** Auswahl oder Drag-Inhalt: Dateien (IDs) und Ordner (Pfade) aus einer Wurzel */
interface Items {
  root: string;
  files: number[];
  folders: string[];
}
interface Menu {
  x: number;
  y: number;
  kind: 'file' | 'folder' | 'blank';
  doc?: DocumentDto;
  folder?: string;
}
const rootKey = (t: string, id: number) => `${t}:${id}`;
const ITEMS_MIME = 'application/x-tww-items';
const sameCur = (a: Cur, b: Cur) => a.root === b.root && a.path === b.path;
const leaf = (f: string) => f.slice(f.lastIndexOf('/') + 1);
const fileKey = (id: number) => `d:${id}`;
const nFiles = (n: number) => `${n} ${n === 1 ? 'Datei' : 'Dateien'}`;
const folderKey = (p: string) => `f:${p}`;

export const GENERATED_NOTE = 'Erzeugt aus Vorlage';
export const isGenerated = (d: { note: string | null }) => !!d.note && d.note.startsWith(GENERATED_NOTE);

export function FilesTab({ entityType, entityId, canWrite, ownLabel = 'Eigene Dateien', toolbarExtra }: { entityType: DocumentEntityType; entityId: number; canWrite: boolean; ownLabel?: string; toolbarExtra?: React.ReactNode }) {
  const qc = useQueryClient();
  const toast = useToast();
  const kinds = useLookup('document_kinds');
  const related = useQuery({ queryKey: ['documents-related', entityType, entityId], queryFn: () => api.get<{ items: RelatedRoot[] }>(`/documents/related${qs({ entity_type: entityType, entity_id: entityId })}`).then((r) => r.items), staleTime: 30_000 });
  const roots = useMemo<Root[]>(
    () => [
      { key: rootKey(entityType, entityId), entity_type: entityType, entity_id: entityId, label: ownLabel, relation: '', href: '', can_write: canWrite, own: true },
      ...(related.data ?? []).map((r) => ({ ...r, key: rootKey(r.entity_type, r.entity_id), own: false })),
    ],
    [entityType, entityId, ownLabel, canWrite, related.data],
  );
  const docQueries = useQueries({
    queries: roots.map((r) => ({ queryKey: ['documents', r.entity_type, r.entity_id], queryFn: () => api.get<{ items: DocumentDto[] }>(`/documents${qs({ entity_type: r.entity_type, entity_id: r.entity_id })}`).then((x) => x.items) })),
  });
  const folderQueries = useQueries({
    queries: roots.map((r) => ({ queryKey: ['folders', r.entity_type, r.entity_id], queryFn: () => api.get<{ items: string[] }>(`/documents/folders${qs({ entity_type: r.entity_type, entity_id: r.entity_id })}`).then((x) => x.items) })),
  });
  const [cur, setCurState] = useState<Cur>({ root: rootKey(entityType, entityId), path: '' });
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<Cur | null>(null);
  const [dragging, setDragging] = useState<Items | null>(null);
  const [menu, setMenu] = useState<Menu | null>(null);
  const [preview, setPreview] = useState<DocumentDto | null>(null);
  const [newFolder, setNewFolder] = useState<string | null>(null);
  const [rename, setRename] = useState<{ path: string; name: string } | null>(null);
  const [move, setMove] = useState<Items | null>(null);
  const [moveTarget, setMoveTarget] = useState<Cur | null>(null);
  const [del, setDel] = useState<Items | null>(null);
  const [kindEdit, setKindEdit] = useState<{ ids: number[]; kind: string } | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  /** Verknüpfte Objekte ohne Dateien und Ordner sind standardmäßig eingeklappt (lange Listen bei Aktionen) */
  const [showEmptyRoots, setShowEmptyRoots] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const setCur = (c: Cur) => {
    setCurState(c);
    setSel(new Set());
    setAnchor(null);
  };

  const rootIdx = Math.max(0, roots.findIndex((r) => r.key === cur.root));
  const root = roots[rootIdx]!;
  const scope = { entity_type: root.entity_type, entity_id: root.entity_id };
  const write = root.can_write;
  const rootOf = (key: string) => roots.find((r) => r.key === key);
  const idxOf = (key: string) => roots.findIndex((r) => r.key === key);
  const docsOf = (i: number) => docQueries[i]?.data ?? [];
  const foldersOf = (i: number) => folderQueries[i]?.data ?? [];
  const invalidate = (r: Root = root) => {
    qc.invalidateQueries({ queryKey: ['documents', r.entity_type, r.entity_id] });
    qc.invalidateQueries({ queryKey: ['folders', r.entity_type, r.entity_id] });
  };
  const fail = (e: Error) => toast(e.message, 'error');
  const countOf = (it: Items) => it.files.length + it.folders.length;
  const describe = (it: Items) => [it.files.length ? `${it.files.length} Datei${it.files.length > 1 ? 'en' : ''}` : '', it.folders.length ? `${it.folders.length} Ordner` : ''].filter(Boolean).join(' und ');

  /* ---------- Mutationen ---------- */
  const upload = useMutation({
    mutationFn: async ({ files, target }: { files: File[]; target: Cur }) => {
      const t = rootOf(target.root)!;
      for (const f of files) {
        const fd = new FormData();
        fd.set('entity_type', t.entity_type);
        fd.set('entity_id', String(t.entity_id));
        fd.set('folder', target.path);
        fd.set('kind', f.type.startsWith('image/') ? 'foto' : 'sonstiges');
        fd.set('file', f);
        await api.upload('/documents', fd);
      }
      return t;
    },
    onSuccess: (t, { files, target }) => {
      invalidate(t);
      toast(`${files.length} Datei${files.length > 1 ? 'en' : ''} abgelegt${t.own ? '' : ` bei ${t.label}`}${target.path ? ` in „${leaf(target.path)}“` : ''}`);
    },
    onError: fail,
  });
  const patch = useMutation({ mutationFn: ({ id, ...b }: { id: number; kind?: string }) => api.patch(`/documents/${id}`, b), onSuccess: () => invalidate(), onError: fail });
  const setKinds = useMutation({
    mutationFn: async ({ ids, kind }: { ids: number[]; kind: string }) => {
      for (const id of ids) await api.patch(`/documents/${id}`, { kind });
    },
    onSuccess: (_r, { ids }) => {
      invalidate();
      setKindEdit(null);
      toast(ids.length > 1 ? `Dokumentart für ${ids.length} Dateien geändert` : 'Dokumentart geändert');
    },
    onError: fail,
  });
  const moveItems = useMutation({
    mutationFn: async ({ items, target }: { items: Items; target: Cur }) => {
      const src = rootOf(items.root)!;
      const t = rootOf(target.root)!;
      for (const id of items.files) await api.patch(`/documents/${id}`, { entity_type: t.entity_type, entity_id: t.entity_id, folder: target.path });
      for (const f of items.folders) await api.post('/documents/folders/move', { entity_type: src.entity_type, entity_id: src.entity_id, from: f, target_entity_type: t.entity_type, target_entity_id: t.entity_id, to: target.path });
      return { src, t };
    },
    onSuccess: ({ src, t }, { items, target }) => {
      invalidate(src);
      invalidate(t);
      setMove(null);
      setSel(new Set());
      toast(`${describe(items)} ${t.key !== src.key ? `zu ${t.label}` : target.path ? `nach „${leaf(target.path)}“` : 'auf die oberste Ebene'} verschoben`);
    },
    onError: fail,
  });
  const deleteItems = useMutation({
    mutationFn: async (items: Items) => {
      const r = rootOf(items.root)!;
      for (const id of items.files) await api.post(`/documents/${id}/archive`);
      for (const f of items.folders) await api.post('/documents/folders/delete', { entity_type: r.entity_type, entity_id: r.entity_id, path: f, force: true });
      return r;
    },
    onSuccess: (r, items) => {
      invalidate(r);
      setDel(null);
      setSel(new Set());
      if (items.folders.includes(cur.path) || items.folders.some((f) => cur.path.startsWith(f + '/'))) setCur({ root: cur.root, path: '' });
      toast(`${describe(items)} gelöscht (Dateien bleiben archiviert)`);
    },
    onError: fail,
  });
  const createFolder = useMutation({
    mutationFn: (name: string) => api.post('/documents/folders', { ...scope, path: cur.path ? `${cur.path}/${name}` : name }),
    onSuccess: (r: unknown) => {
      invalidate();
      setNewFolder(null);
      setCur({ root: root.key, path: (r as { path: string }).path });
    },
    onError: fail,
  });
  const renameFolder = useMutation({
    mutationFn: ({ path, name }: { path: string; name: string }) => api.post('/documents/folders/rename', { ...scope, from: path, to: path.includes('/') ? path.slice(0, path.lastIndexOf('/') + 1) + name : name }),
    onSuccess: (r: unknown, { path }) => {
      invalidate();
      setRename(null);
      if (cur.path === path || cur.path.startsWith(path + '/')) setCur({ root: root.key, path: (r as { path: string }).path + cur.path.slice(path.length) });
    },
    onError: fail,
  });

  /* ---------- Ableitungen ---------- */
  const all = docsOf(rootIdx);
  const allFolders = foldersOf(rootIdx);
  const countIn = (docs: DocumentDto[], path: string) => docs.filter((d) => d.folder === path || d.folder.startsWith(path + '/')).length;
  const childrenOf = (folders: string[], path: string) => folders.filter((f) => (path ? f.startsWith(path + '/') && !f.slice(path.length + 1).includes('/') : !f.includes('/')));
  const here = useMemo(() => all.filter((d) => d.folder === cur.path).sort((a, b) => (a.title || a.original_name).localeCompare(b.title || b.original_name, 'de')), [all, cur.path]);
  const hereFolders = childrenOf(allFolders, cur.path);
  const order = useMemo(() => [...hereFolders.map(folderKey), ...here.map((d) => fileKey(d.id))], [hereFolders, here]);
  const crumbs = cur.path ? cur.path.split('/') : [];
  const nameOf = (d: DocumentDto) => d.title || d.original_name;
  const docName = (id: number) => {
    const d = all.find((x) => x.id === id);
    return d ? nameOf(d) : '';
  };
  const selItems = (): Items => ({ root: cur.root, files: here.filter((d) => sel.has(fileKey(d.id))).map((d) => d.id), folders: hereFolders.filter((f) => sel.has(folderKey(f))) });
  const itemsOf = (m: Menu): Items => {
    const key = m.kind === 'file' ? fileKey(m.doc!.id) : m.kind === 'folder' ? folderKey(m.folder!) : '';
    if (key && sel.has(key)) return selItems();
    return { root: cur.root, files: m.kind === 'file' ? [m.doc!.id] : [], folders: m.kind === 'folder' ? [m.folder!] : [] };
  };

  /* ---------- Auswahl ---------- */
  const toggleSel = (key: string, e?: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }) => {
    setSel((s) => {
      const n = new Set(s);
      if (e?.shiftKey && anchor && order.includes(anchor)) {
        const a = order.indexOf(anchor);
        const b = order.indexOf(key);
        for (const k of order.slice(Math.min(a, b), Math.max(a, b) + 1)) n.add(k);
        return n;
      }
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
    if (!e?.shiftKey) setAnchor(key);
  };
  const rowClick = (key: string, e: MouseEvent, open: () => void) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      e.preventDefault();
      toggleSel(key, e);
      return;
    }
    if (sel.size) {
      setSel(new Set([key]));
      setAnchor(key);
      return;
    }
    open();
  };
  const allSelected = order.length > 0 && order.every((k) => sel.has(k));

  /* ---------- Kontextmenü ---------- */
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && close();
    // Erst nach dem öffnenden Klick registrieren, sonst schließt derselbe Klick das Menü sofort wieder
    const t = setTimeout(() => {
      document.addEventListener('click', close);
      document.addEventListener('contextmenu', close);
    }, 0);
    document.addEventListener('keydown', key);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close);
      document.removeEventListener('keydown', key);
    };
  }, [menu]);
  const openMenu = (e: MouseEvent, m: Omit<Menu, 'x' | 'y'>) => {
    e.preventDefault();
    e.stopPropagation();
    const key = m.kind === 'file' ? fileKey(m.doc!.id) : m.kind === 'folder' ? folderKey(m.folder!) : null;
    if (key && !sel.has(key)) {
      setSel(new Set([key]));
      setAnchor(key);
    }
    setMenu({ ...m, x: Math.min(e.clientX, window.innerWidth - 240), y: Math.min(e.clientY, window.innerHeight - 260) });
  };

  /* ---------- Drag-and-Drop ---------- */
  const canDropAt = (target: Cur, items?: Items | null) => {
    if (!rootOf(target.root)?.can_write) return false;
    if (items && items.root === target.root) {
      for (const f of items.folders) if (target.path === f || target.path.startsWith(f + '/')) return false;
    }
    return true;
  };
  const dragProps = (target: Cur) => ({
    onDragOver: (e: DragEvent) => {
      const isItems = e.dataTransfer.types.includes(ITEMS_MIME);
      const isFiles = e.dataTransfer.types.includes('Files');
      if (!isItems && !isFiles) return;
      if (!canDropAt(target, isItems ? dragging : null)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = isItems ? 'move' : 'copy';
      if (!dropAt || !sameCur(dropAt, target)) setDropAt(target);
    },
    onDragLeave: (e: DragEvent) => {
      if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setDropAt((d) => (d && sameCur(d, target) ? null : d));
    },
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDropAt(null);
      const raw = e.dataTransfer.getData(ITEMS_MIME);
      setDragging(null);
      if (raw) {
        const items = JSON.parse(raw) as Items;
        if (!canDropAt(target, items)) return;
        const r = rootOf(items.root);
        const srcDocs = r ? docsOf(idxOf(items.root)) : [];
        // Nichts tun, wenn alles schon dort liegt
        const filesStay = items.files.every((id) => srcDocs.find((d) => d.id === id)?.folder === target.path) && items.root === target.root;
        const foldersStay = items.folders.every((f) => (f.includes('/') ? f.slice(0, f.lastIndexOf('/')) : '') === target.path) && items.root === target.root;
        if (filesStay && foldersStay) return;
        moveItems.mutate({ items, target });
        return;
      }
      if (!canDropAt(target)) return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length) upload.mutate({ files, target });
    },
  });
  const dropCls = (target: Cur) => (dropAt && sameCur(dropAt, target) ? 'drop-over' : '');
  const rowDrag = (key: string, single: Items) =>
    write
      ? {
          draggable: true,
          onDragStart: (e: DragEvent) => {
            const items = sel.has(key) ? selItems() : single;
            e.dataTransfer.setData(ITEMS_MIME, JSON.stringify(items));
            e.dataTransfer.effectAllowed = 'move';
            setDragging(items);
          },
          onDragEnd: () => setDragging(null),
        }
      : {};
  const isDragged = (key: string) => (dragging ? (key.startsWith('d:') ? dragging.files.includes(Number(key.slice(2))) : dragging.folders.includes(key.slice(2))) && dragging.root === cur.root : false);

  const renderTree = (r: Root, i: number, path: string, depth: number): React.ReactNode => (
    <>
      {childrenOf(foldersOf(i), path).map((f) => {
        const t = { root: r.key, path: f };
        return (
          <div key={f}>
            <button type="button" className={`${cur.root === r.key && cur.path === f ? 'on' : ''} ${dropCls(t)}`} style={{ paddingLeft: 10 + depth * 14 }} onClick={() => setCur(t)} onContextMenu={(e) => (setCurState(t), openMenu(e, { kind: 'blank' }))} {...dragProps(t)}>
              <Icon name="folder" className="icon-folder" /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{leaf(f)}</span>
              <span className="n">{countIn(docsOf(i), f) || ''}</span>
            </button>
            {renderTree(r, i, f, depth + 1)}
          </div>
        );
      })}
    </>
  );

  if (docQueries[0]?.isLoading || folderQueries[0]?.isLoading) return <Skeleton rows={4} />;
  if (docQueries[0]?.isError) return <ErrorState error={docQueries[0].error} retry={() => docQueries[0]!.refetch()} />;

  const kindSelect = (d: DocumentDto) =>
    write ? <Select label="Dokumentart" value={d.kind} onChange={(v) => patch.mutate({ id: d.id, kind: v })} options={kinds.values.map((k) => ({ value: k.code, label: k.label }))} placeholder="wählen" className="fp-kind" /> : null;
  const targetFolders = moveTarget ? foldersOf(idxOf(moveTarget.root)).filter((f) => !(move && move.root === moveTarget.root && move.folders.some((m) => f === m || f.startsWith(m + '/')))) : [];

  return (
    <div className={`files ${dragging ? 'dragging' : ''}`}>
      <div className="tree">
        {roots.map((r, i) => {
          const isCur = cur.root === r.key;
          const isEmpty = !r.own && docsOf(i).length === 0 && foldersOf(i).length === 0 && !isCur;
          if (isEmpty && !showEmptyRoots) return null;
          const hidden = !r.own && collapsed[r.key];
          const t = { root: r.key, path: '' };
          return (
            <div key={r.key} className={`froot ${r.own ? 'own' : ''}`}>
              {!r.own && roots.findIndex((x, j) => !x.own && (showEmptyRoots || cur.root === x.key || docsOf(j).length > 0 || foldersOf(j).length > 0)) === i && <div className="froot-head">Verknüpfte Objekte</div>}
              <button type="button" className={`${isCur && cur.path === '' ? 'on' : ''} ${dropCls(t)}`} onClick={() => (setCur(t), setCollapsed((c) => ({ ...c, [r.key]: false })))} onContextMenu={(e) => (setCurState(t), openMenu(e, { kind: 'blank' }))} {...dragProps(t)}>
                <Icon name={r.own ? 'folder' : r.entity_type === 'contact' ? 'user' : r.entity_type === 'area' ? 'map' : r.entity_type === 'site_visit' ? 'doc' : r.entity_type === 'monitoring' ? 'chart' : 'tasks'} className={r.own ? 'icon-folder' : ''} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  {r.label}
                  {!r.own && <span className="d">{r.relation}</span>}
                </span>
                <span className="n">{docsOf(i).length || ''}</span>
              </button>
              {!hidden && renderTree(r, i, '', 1)}
              {!r.own && !hidden && foldersOf(i).length > 0 && (
                <button type="button" className="mut small" style={{ paddingLeft: 24 }} onClick={() => setCollapsed((c) => ({ ...c, [r.key]: true }))}>
                  einklappen
                </button>
              )}
            </div>
          );
        })}
        {(() => {
          const empties = roots.filter((r, i) => !r.own && docsOf(i).length === 0 && foldersOf(i).length === 0 && cur.root !== r.key);
          if (!empties.length) return null;
          return (
            <button type="button" className="mut small" style={{ marginTop: 6 }} onClick={() => setShowEmptyRoots((v) => !v)}>
              {showEmptyRoots ? 'Objekte ohne Dateien ausblenden' : `${empties.length} verknüpfte Objekte ohne Dateien anzeigen`}
            </button>
          );
        })()}
      </div>
      <div className="main" {...(write ? dragProps(cur) : {})} onContextMenu={(e) => openMenu(e, { kind: 'blank' })}>
        {!root.own && (
          <div className="froot-banner">
            <Icon name="link" /> Ordner von <Link className="link" to={root.href}>{root.label}</Link> ({root.relation}). Dateien hier landen beim verknüpften Objekt, nicht bei diesem Datensatz.
          </div>
        )}
        <div className="toolbar">
          <div className="crumbs" aria-label="Pfad">
            <button type="button" onClick={() => setCur({ root: root.key, path: '' })} className={`${cur.path ? '' : 'cur'} ${dropCls({ root: root.key, path: '' })}`} {...dragProps({ root: root.key, path: '' })}>
              {root.own ? 'Alle Dateien' : root.label}
            </button>
            {crumbs.map((c, i) => {
              const p = crumbs.slice(0, i + 1).join('/');
              const t = { root: root.key, path: p };
              return (
                <span key={p}>
                  <span className="mut">/</span>{' '}
                  <button type="button" onClick={() => setCur(t)} className={`${p === cur.path ? 'cur' : ''} ${dropCls(t)}`} {...dragProps(t)}>
                    {c}
                  </button>
                </span>
              );
            })}
          </div>
          {root.own && toolbarExtra}
          {write && (
            <>
              <button type="button" className="btn sm" onClick={() => setNewFolder('')}>
                <Icon name="plus" /> Ordner
              </button>
              <button type="button" className="btn sm primary" onClick={() => fileInput.current?.click()}>
                <Icon name="upload" /> Hochladen
              </button>
            </>
          )}
          <input ref={fileInput} type="file" multiple hidden onChange={(e) => e.target.files && upload.mutate({ files: Array.from(e.target.files), target: cur })} />
        </div>
        {sel.size > 0 && (
          <div className="selbar">
            <strong>{describe(selItems()) || `${sel.size} ausgewählt`}</strong>
            <span className="mut small">ausgewählt</span>
            <span style={{ flex: 1 }} />
            {write && (
              <>
                <button type="button" className="btn sm" onClick={() => (setMove(selItems()), setMoveTarget({ root: cur.root, path: cur.path }))}>
                  <Icon name="folder" /> Verschieben
                </button>
                <button type="button" className="btn sm danger" onClick={() => setDel(selItems())}>
                  <Icon name="trash" /> Löschen
                </button>
              </>
            )}
            <button type="button" className="btn sm" onClick={() => setSel(new Set())}>
              Auswahl aufheben
            </button>
          </div>
        )}
        {here.length === 0 && hereFolders.length === 0 ? (
          <EmptyState>{cur.path ? 'Dieser Ordner ist leer.' : root.own ? 'Noch keine Dateien. Verträge, Protokolle und Fotos gehören hierher.' : `Noch keine Dateien bei ${root.label}.`}</EmptyState>
        ) : (
          <div className="tw">
            <table className="ftable">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" aria-label="Alle auswählen" checked={allSelected} onChange={(e) => setSel(e.target.checked ? new Set(order) : new Set())} />
                  </th>
                  <th style={{ width: 52 }}></th>
                  <th>Name</th>
                  <th>Datum</th>
                  <th>Von</th>
                  <th className="num">Größe</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {hereFolders.map((f) => {
                  const t = { root: root.key, path: f };
                  const key = folderKey(f);
                  return (
                    <tr key={f} className={`row folder ${sel.has(key) ? 'selected' : ''} ${isDragged(key) ? 'is-dragging' : ''} ${dropCls(t)}`} onClick={(e) => rowClick(key, e, () => setCur(t))} onContextMenu={(e) => openMenu(e, { kind: 'folder', folder: f })} {...rowDrag(key, { root: cur.root, files: [], folders: [f] })} {...dragProps(t)}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Ordner ${leaf(f)} auswählen`} checked={sel.has(key)} onChange={() => toggleSel(key)} />
                      </td>
                      <td>
                        <Icon name="folder" className="icon-folder" />
                      </td>
                      <td className="name" colSpan={3}>
                        <span className="t">{leaf(f)}</span>
                      </td>
                      <td className="mut small num">{nFiles(countIn(all, f))}</td>
                      <td>
                        <div className="row-actions">
                          {write && (
                            <button type="button" className="icon-btn" aria-label="Mehr" title="Mehr" onClick={(e) => openMenu(e, { kind: 'folder', folder: f })}>
                              <Icon name="menu" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {here.map((d) => {
                  const key = fileKey(d.id);
                  return (
                    <tr key={d.id} className={`row file ${sel.has(key) ? 'selected' : ''} ${isDragged(key) ? 'is-dragging' : ''}`} onClick={(e) => rowClick(key, e, () => setPreview(d))} onContextMenu={(e) => openMenu(e, { kind: 'file', doc: d })} {...rowDrag(key, { root: cur.root, files: [d.id], folders: [] })} title={write ? 'Klick: Vorschau · Strg-Klick: auswählen · Ziehen: verschieben' : 'Klick: Vorschau'}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`${nameOf(d)} auswählen`} checked={sel.has(key)} onChange={() => toggleSel(key)} />
                      </td>
                      <td>
                        <FileIcon d={d} />
                      </td>
                      <td className="name">
                        <span className="t">
                          {nameOf(d)}
                          {isGenerated(d) && <span className="gen" title={d.note || 'Automatisch aus Vorlage erzeugt'}>erzeugt</span>}
                        </span>
                        <span className="sm">{KIND_NAME[fileKind(d)]}{d.kind && d.kind !== 'sonstiges' && d.kind !== 'foto' ? ` · ${kinds.values.find((k) => k.code === d.kind)?.label ?? d.kind}` : ''}</span>
                      </td>
                      <td className="small">{fmtDate(d.taken_at ?? d.created_at)}</td>
                      <td className="small mut">{d.created_by_name ?? ''}</td>
                      <td className="num small mut">{d.size_bytes ? `${fmtNum(Math.round(d.size_bytes / 1024))} KB` : ''}</td>
                      <td>
                        <div className="row-actions">
                          <a className="icon-btn" href={`/api/v1/documents/${d.id}/file?download=1`} aria-label="Herunterladen" title="Herunterladen" onClick={(e) => e.stopPropagation()}>
                            <Icon name="download" />
                          </a>
                          {write && (
                            <button type="button" className="icon-btn" aria-label="Mehr" title="Mehr" onClick={(e) => openMenu(e, { kind: 'file', doc: d })}>
                              <Icon name="menu" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {write && (
          <div className={`dropzone drop ${dropAt && sameCur(dropAt, cur) ? 'over' : ''}`} onClick={() => fileInput.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && fileInput.current?.click()}>
            <Icon name="upload" /> {upload.isPending ? 'Wird hochgeladen …' : `Dateien hierher ziehen (${root.own ? '' : `${root.label}, `}${cur.path ? `Ordner „${leaf(cur.path)}“` : 'oberste Ebene'}) oder auf einen Ordner links`}
          </div>
        )}
      </div>

      {menu && (
        <div className="ctx" style={{ left: menu.x, top: menu.y }} role="menu" onClick={(e) => e.stopPropagation()}>
          {menu.kind === 'file' && (
            <>
              <button type="button" role="menuitem" onClick={() => (setMenu(null), setPreview(menu.doc!))}>
                <Icon name="search" /> Vorschau
              </button>
              <a role="menuitem" href={`/api/v1/documents/${menu.doc!.id}/file?download=1`} onClick={() => setMenu(null)}>
                <Icon name="download" /> Herunterladen
              </a>
            </>
          )}
          {menu.kind === 'folder' && (
            <>
              <button type="button" role="menuitem" onClick={() => (setMenu(null), setCur({ root: cur.root, path: menu.folder! }))}>
                <Icon name="folder" /> Öffnen
              </button>
              {write && (
                <button type="button" role="menuitem" onClick={() => (setMenu(null), setRename({ path: menu.folder!, name: leaf(menu.folder!) }))}>
                  <Icon name="edit" /> Umbenennen
                </button>
              )}
            </>
          )}
          {write && menu.kind === 'file' && itemsOf(menu).folders.length === 0 && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                const ids = itemsOf(menu).files;
                const first = here.find((d) => d.id === ids[0]);
                const same = ids.every((id) => here.find((d) => d.id === id)?.kind === first?.kind);
                setMenu(null);
                setKindEdit({ ids, kind: same ? (first?.kind ?? '') : '' });
              }}
            >
              <Icon name="doc" /> Dokumentart{itemsOf(menu).files.length > 1 ? ` (${itemsOf(menu).files.length})` : ''} …
            </button>
          )}
          {write && menu.kind !== 'blank' && (
            <>
              <button type="button" role="menuitem" onClick={() => (setMenu(null), setMove(itemsOf(menu)), setMoveTarget({ root: cur.root, path: cur.path }))}>
                <Icon name="link" /> Verschieben{countOf(itemsOf(menu)) > 1 ? ` (${countOf(itemsOf(menu))})` : ''} …
              </button>
              <button type="button" role="menuitem" className="danger" onClick={() => (setMenu(null), setDel(itemsOf(menu)))}>
                <Icon name="trash" /> Löschen{countOf(itemsOf(menu)) > 1 ? ` (${countOf(itemsOf(menu))})` : ''}
              </button>
              <div className="sep" />
            </>
          )}
          {write && (
            <>
              <button type="button" role="menuitem" onClick={() => (setMenu(null), setNewFolder(''))}>
                <Icon name="plus" /> Neuer Ordner{cur.path ? ` in „${leaf(cur.path)}“` : ''}
              </button>
              <button type="button" role="menuitem" onClick={() => (setMenu(null), fileInput.current?.click())}>
                <Icon name="upload" /> Hochladen
              </button>
            </>
          )}
          {order.length > 0 && (
            <button type="button" role="menuitem" onClick={() => (setMenu(null), setSel(allSelected ? new Set() : new Set(order)))}>
              <Icon name="check" /> {allSelected ? 'Auswahl aufheben' : 'Alles auswählen'}
            </button>
          )}
        </div>
      )}

      {newFolder !== null && (
        <Modal
          title="Neuer Ordner"
          subtitle={`${root.own ? '' : `bei ${root.label} · `}${cur.path ? `in ${cur.path}` : 'auf oberster Ebene'}`}
          onClose={() => setNewFolder(null)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setNewFolder(null)}>
                Abbrechen
              </button>
              <button className="btn primary" type="button" disabled={!newFolder.trim() || createFolder.isPending} onClick={() => createFolder.mutate(newFolder.trim())}>
                Anlegen
              </button>
            </>
          }
        >
          <div className="form one">
            <TextInput label="Name" value={newFolder} onChange={setNewFolder} autoFocus onEnter={() => newFolder.trim() && createFolder.mutate(newFolder.trim())} placeholder="z. B. Verträge" />
          </div>
        </Modal>
      )}
      {rename && (
        <Modal
          title="Ordner umbenennen"
          subtitle={rename.path}
          onClose={() => setRename(null)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setRename(null)}>
                Abbrechen
              </button>
              <button className="btn primary" type="button" disabled={!rename.name.trim() || renameFolder.isPending} onClick={() => renameFolder.mutate({ path: rename.path, name: rename.name.trim() })}>
                Speichern
              </button>
            </>
          }
        >
          <div className="form one">
            <TextInput label="Neuer Name" value={rename.name} onChange={(v) => setRename({ ...rename, name: v })} autoFocus onEnter={() => rename.name.trim() && renameFolder.mutate({ path: rename.path, name: rename.name.trim() })} />
          </div>
        </Modal>
      )}
      {move && moveTarget && (
        <Modal
          title={`${describe(move)} verschieben`}
          onClose={() => setMove(null)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setMove(null)}>
                Abbrechen
              </button>
              <button className="btn primary" type="button" disabled={moveItems.isPending || !canDropAt(moveTarget, move)} onClick={() => moveItems.mutate({ items: move, target: moveTarget })}>
                Verschieben
              </button>
            </>
          }
        >
          <div className="form one">
            <Select label="Objekt" value={moveTarget.root} placeholder="wählen" onChange={(v) => setMoveTarget({ root: v, path: '' })} options={roots.filter((r) => r.can_write).map((r) => ({ value: r.key, label: r.own ? ownLabel : `${r.label} (${r.relation})` }))} hint="Verknüpfte Objekte mit Schreibrecht; schneller geht es per Ziehen auf einen Ordner links" />
            <Select label="Ordner" value={moveTarget.path} placeholder="Oberste Ebene" onChange={(v) => setMoveTarget({ ...moveTarget, path: v })} options={targetFolders.map((f) => ({ value: f, label: f }))} />
          </div>
        </Modal>
      )}
      {kindEdit && (
        <Modal
          title="Dokumentart"
          subtitle={kindEdit.ids.length > 1 ? `${kindEdit.ids.length} Dateien` : docName(kindEdit.ids[0]!)}
          onClose={() => setKindEdit(null)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setKindEdit(null)}>
                Abbrechen
              </button>
              <button className="btn primary" type="button" disabled={!kindEdit.kind || setKinds.isPending} onClick={() => setKinds.mutate(kindEdit)}>
                Speichern
              </button>
            </>
          }
        >
          <div className="form one">
            <Select label="Dokumentart" value={kindEdit.kind} onChange={(v) => setKindEdit({ ...kindEdit, kind: v })} options={kinds.values.map((k) => ({ value: k.code, label: k.label }))} placeholder="wählen" />
          </div>
        </Modal>
      )}
      {del && (
        <ConfirmDialog
          title="Löschen"
          text={
            <>
              {describe(del)} löschen?
              {del.folders.length > 0 && <div className="mut small" style={{ marginTop: 6 }}>Ordner werden mit allen Unterordnern entfernt.</div>}
              <div className="mut small" style={{ marginTop: 6 }}>Dateien werden archiviert und bleiben für die Administration wiederherstellbar.</div>
            </>
          }
          confirmLabel="Löschen"
          danger
          onConfirm={() => deleteItems.mutate(del)}
          onClose={() => setDel(null)}
          busy={deleteItems.isPending}
        />
      )}
      {preview && <FilePreview doc={preview} list={here} onNavigate={setPreview} onClose={() => setPreview(null)} extra={kindSelect(preview)} />}
    </div>
  );
}
